// Layered Read deep links: `?lr=<target>` on /view_analysis and /icicle.
//
// Exists so a support email, a tour or a help article can send the writer to one exact
// place in the read instead of describing how to click there. The grammar is deliberately
// small and written in the units the writer can SEE (scene 35, Act 2b, the Patterns tab):
//
//   lr=scene-35                  open Scene 35's reading      (also scene35, sc35, 35)
//   lr=scene-35:pressure         …focused on one axis
//   lr=seq-2b-7   lr=act-2b      open (and zoom into) that sequence / act
//   lr=script                    the whole-script reading
//   lr=findings                  the Findings tab
//   lr=axes   lr=axes:pressure   the Axes tab, optionally opened on one axis
//   lr=patterns                  Patterns overview
//   lr=patterns:scene            Patterns at one grain (scene | sequence | act | script)
//   lr=patterns:scene:2          …with the 2nd recurring pattern opened
//   lr=patterns:scene:other      …with the "one more, smaller pattern" block opened
//
// Parsing is pure and has no React dependency so `deepLink.selftest.js` can run it in node.
// An unknown or unresolvable target resolves to null and the widget lands where it always
// did: a bad link must never be worse than no link.

const GRAINS = { overview: 'overview', scene: 'scene', scenes: 'scene', sequence: 'sequence', sequences: 'sequence',
  seq: 'sequence', act: 'act', acts: 'act', script: 'script' };

export function parseLrTarget(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  const parts = s.split(':').map(p => p.trim());
  const head = parts[0];
  if (head === 'findings') return { kind: 'findings' };
  if (head === 'axes' || head === 'axis') return { kind: 'axes', axis: parts[1] || null };
  if (head === 'patterns' || head === 'pattern') {
    const grain = GRAINS[parts[1] || 'overview'] || null;
    if (!grain) return null;
    const sub = parts[2] || '';
    let focus = null;
    if (sub === 'other' || sub === 'more') focus = { other: true };
    else if (/^\d+$/.test(sub) && Number(sub) >= 1) focus = { habit: Number(sub) };
    return { kind: 'patterns', grain, focus };
  }
  return { kind: 'unit', id: head, axis: parts[1] || null };
}

// Canonical DER unit id for a writer-typed unit reference, or null if it is not one.
export function canonicalUnitId(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'script' || s === 'movie' || s === 'whole') return 'script';
  let m;
  if ((m = s.match(/^(?:scene|sc|s)?-?(\d+)$/))) return `scene-${Number(m[1])}`;
  if ((m = s.match(/^(?:sequence|seq)-?([0-9a-z][0-9a-z-]*)$/))) return `seq-${m[1]}`;
  if ((m = s.match(/^act-?([0-9a-z]+)$/))) return `act-${m[1]}`;
  return s || null;
}

// Find the node a unit target names. Matches the node id, its DER unit_id, and — for
// scenes — the scene number, because a stage play's ids are not `scene-N`.
export function resolveLrUnit(nodes, raw) {
  if (!Array.isArray(nodes) || !nodes.length) return null;
  const want = canonicalUnitId(raw);
  if (!want) return null;
  const lc = v => (v == null ? '' : String(v).toLowerCase());
  if (want === 'script') return nodes.find(n => n.nodeType === 'movie') || null;
  const byId = nodes.find(n => lc(n.id) === want || lc(n.unitId) === want || lc(n.detailedContent?.unit_id) === want);
  if (byId) return byId;
  const m = want.match(/^scene-(\d+)$/);
  if (m) {
    const num = Number(m[1]);
    return nodes.find(n => n.nodeType === 'scene' && Number(n.detailedContent?.number) === num) || null;
  }
  return null;
}
